/** @jsxImportSource @emotion/react */

import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Scroll, { ScrollContainer as ChocieScrollContainer } from '@component/common/scroll';
import * as size from '@constant/size';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import * as color from '@constant/color';
import * as animation from '@constant/animation';
import otterReadImage from '@asset/image/otter-read.png';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import ImageTextBlock from '@component/common/block/image-text-block';
import Callout from '@design-system/callout';
import Canvas from '@design-system/canvas';
import Gap from '@component/common/gap';
import Difficulty from '@component/iyagigo/cloze/study/difficulty';
import VocabularyChoice from '@component/iyagigo/cloze/study/vocabulary-choice';
import GrammarChoice from '@component/iyagigo/cloze/study/grammar-choice';
import OtterNudge from '@component/iyagigo/cloze/study/otter-nudge';
import BottomContainer from '@component/common/architecture/bottom-container';
import {
  useModifyClozeWorkbookMutation,
  useBookmarkClozeWorkbookQuery,
  useIncorrectNoteClozeWorkbookQuery,
  useTodayClozeWorkbookQuery,
} from '@hook/iyagigo/useApi';
import { completeSentence, markBlanks, normalizeCloze, sanitizeSentencePronunciation } from '@util/string';
import {
  DAY_MS,
  calculateClozeReviewAfter,
  getClozeTypeBySubject,
  getDiffDays,
  sanitizeSentence,
} from '@util/workbook';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useNavigate } from 'react-router-dom';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { v4 as uuid4 } from 'uuid';
import { IYAGIGO_PATH, createClozePath } from '@constant/iyagigo/route-path';
import { GUEST_VOCABULARY_CLOZE_QUOTA, GUEST_GRAMMAR_CLOZE_QUOTA, IS_MOBILE_MOCKING } from '@constant/iyagigo/variable';
import { useApplicationPurchase } from '@hook/common/usePurchase';
import { useMySubscriptionQuery } from '@hook/billing/useApi';
import { EApiSubscriptionStatus } from '@datatype/billing/api-type';
// import { hasConsumedTodayConversation, markTodayConversationConsumed } from '@util/conversation-daily-limit';
import { EWorkbookItemType, EWorkbookStudyType, TIyagigoSubMenu } from '@datatype/iyagigo/type';
import { useActiveTime } from '@hook/common/useEvent';
import { usePlaySound } from '@hook/common/useSound';
import { EApiLanguageCode, EApiStudyType } from '@datatype/iyagigo/api-type';
import ConversationPractice from '@component/iyagigo/cloze/conversation-study';
import Explanation from '@component/iyagigo/common/explanation';
import { ETheme } from '@datatype/type';
import TextButton from '@design-system/text-button';
import ImageAudioButton from '@design-system/image-audio-button';
import IconButton from '@design-system/icon-button';
import { useCheckAttendance, useRecordStudy, useWorkbookSync } from '@hook/iyagigo/useStudent';
import { getAttendanceType } from '@util/attendance';
import Chip from '@design-system/chip';
import { GRAMMAR_CHIP_HEIGHT_JP, GRAMMAR_CHIP_HEIGHT_EN, GRAMMAR_CHIP_PADDING } from '@constant/design';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import Line from '@component/common/line';

interface IProps {
  mode: TIyagigoSubMenu['cloze'];
  workbookIdx: number;
  setWorkbookIdx: React.Dispatch<React.SetStateAction<number>>;
}

// ############### variable ###############
const TEXT_PADDING = '5px 10px';

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} 0 ${size.PAGE_PADDING};
  display: flex;
  flex-flow: column nowrap;
`;

const ClozeContentContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-flow: column nowrap;
`;

const CardQuestionContainerA = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  //padding: ${size.MARGIN_1};
  width: 100%;
`;

const CardQuestionContainerB = styled(CardQuestionContainerA)`
  min-height: 120px;
`;

const GrammarSentenceContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
  margin: 0 0 ${size.MARGIN_1} 0;
`;

const SentenceContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
  //line-height: 2;
`;

const ChoiceContainer = styled.div`
  display: flex;
  width: 100%;
`;

const OtterNudgeContainer = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  left: ${size.PAGE_PADDING};
  bottom: 180px;
  z-index: 5;

  ${(props) => (props.$isClosing ? animation.NUDGE_OUT_CSS : animation.NUDGE_IN_CSS)}
`;

const SimpleExplanationContainer = styled.div<{ $backgroundColor: string }>`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${({ $backgroundColor }) => $backgroundColor};

  ${animation.CONTAINER_FADE_IN_CSS}
`;

// 이전: 정답 문장을 흰 배경 + 초록 테두리 박스로 강조했던 스타일 (기록용 보존)
// const GrammarAnswerContainer = styled.div<{ show: boolean }>`
//   visibility: ${(/* props */) => '/* show? visible : hidden */'};
//   padding: ${TEXT_PADDING};
//   background-color: ${color.WHITE_1};
//   border-color: ${color.GREEN_4};
//   border-radius: ${size.BORDER_RADIUS_1};
//   border-width: ${size.BORDER_WIDTH_1};
//   border-style: solid;
// `;

const BottomContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: ${size.MARGIN_1};
  padding: 0 ${size.PAGE_PADDING} ${IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.MARGIN_4} ${size.PAGE_PADDING};
`;

const ControlContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const FirstRowContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  //align-items: flex-end;
  align-items: center;
  width: 100%;
`;

const BookmarkContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 2px;
  margin-left: auto;
`;

// 상단 메타 줄 우측 chip 묶음 (학습 간격 + 다음 복습 시점)
const MetaChipContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 5px;
`;

const BookmarkCalloutContainer = styled.div`
  position: absolute;
  bottom: calc(100% + 5px);
  right: 0;
  z-index: 10;
  width: max-content;
`;

const StudyContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const ControlRowContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  width: 100%;
`;

const NavigationButtonContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const QUESTION_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BROWN_1, bold: true },
  box: { alignX: 'left', margin: '0 0 0 0' },
} as const;

const QUESTION_STUDY_LANGUAGE_TEXT_DESIGN = {
  text: { size: 'large', color: color.BLACK_2, bold: true },
  box: { alignX: 'left', margin: '0 0 0 0' },
} as const;

const PRONUNCIATION_TEXT_DESIGN = {
  text: { size: 'medium', color: color.GRAY_1, bold: false },
  box: { alignX: 'left', margin: '0 0 0 0' },
} as const;

const SENTENCE_TEXT_DESIGN = {
  text: { size: 'large', color: color.BLACK_2, bold: true },
  box: { alignX: 'left' },
} as const;

// 듣기 정답 공개 시 보여주는 한국어 번역. 학습언어 문장보다 약하게 표시한다.
const TRANSLATION_TEXT_DESIGN = {
  text: { size: 'medium', color: color.GRAY_1, bold: false },
  box: { alignX: 'left' },
} as const;

// ############### design function ###############
const NAVIGATION_BUTTON_WIDTH = '80px';
const CONVERSATION_CONTROL_BUTTON_WIDTH = '55px';

const createGrammarSentenceWordDesign = (enable: boolean, isJapanese = false) => {
  return {
    text: {
      size: 'medium',
      color: color.BLACK_2,
      bold: true,
    },
    box: {
      alignX: 'center',
      padding: GRAMMAR_CHIP_PADDING,
      height: isJapanese ? GRAMMAR_CHIP_HEIGHT_JP : GRAMMAR_CHIP_HEIGHT_EN,
      backgroundColor: color.WHITE_1,
      borderColor: color.YELLOW_2,
      borderRadius: size.BORDER_RADIUS_1,
      borderWidth: size.BORDER_WIDTH_1,
      event:
        enable &&
        ({
          cursor: 'pointer',
          hover: `
        box-shadow: ${color.SURFACE_SHADOW};
        transform: translateY(-1px);
      `,
        } as any),
    },
  } as const;
};

const MetaContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const Cloze: React.FC<IProps> = ({ mode, workbookIdx, setWorkbookIdx }) => {
  // ############### state ###############
  const dispatch = useTypedDispatch();
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const subscriptionQuery = useMySubscriptionQuery();
  const isSubscriber = subscriptionQuery.data?.data?.status === EApiSubscriptionStatus.ACTIVE;
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const clozeWorkbookStudyRecord =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.clozeWorkbookStudyRecord) || {};
  const workbookStudyType =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.workbookStudyType) ??
    EWorkbookStudyType.TODAY_STUDY;
  const workbookItemType =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.workbookItemType) ?? EWorkbookItemType.BUNDLE;
  const currentClozeNoteWorkbookKey =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.currentClozeNoteWorkbookKey) ?? null;

  const isSingleItem = workbookItemType === EWorkbookItemType.SINGLE;
  const isBundleItem = workbookItemType === EWorkbookItemType.BUNDLE;
  const isClozeNote =
    workbookStudyType === EWorkbookStudyType.BOOKMARK_NOTE || workbookStudyType === EWorkbookStudyType.INCORRECT_NOTE;
  const isRepeatStudy = isClozeNote && isBundleItem;
  const isTodayStudy = workbookStudyType === EWorkbookStudyType.TODAY_STUDY;
  const isTodayReview = workbookStudyType === EWorkbookStudyType.TODAY_REVIEW;
  const { getActiveTime, reset: resetActiveTime } = useActiveTime(true);
  const playSound = usePlaySound();
  const checkClozeAttendance = useCheckAttendance();
  const recordStudy = useRecordStudy();
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const { status: purchaseStatus, isApp } = useApplicationPurchase();

  useEffect(() => {
    dispatch(modalActions.hideOverlay());
  }, []);

  // mount 시 학습 시작 표시, unmount 시 자동 해제
  useEffect(() => {
    if (!mode) return;
    // if (!isTodayReview) dispatch(workbookActions.storeIsStudying(true));
    dispatch(workbookActions.storeIsStudying(true));
    return () => {
      dispatch(workbookActions.storeIsStudying(false));
    };
  }, [mode]);

  // [TEST] IS_TEST=true이면 Cloze 진입 시 축하 모달 미리보기
  useEffect(() => {
    if (!IS_MOBILE_MOCKING || !mode) return;
    dispatch(
      modalActions.showContentModal({
        type: 'study-celebration',
        data: { title: '오늘도 해냈어요!', description: '<strong>10문제</strong> 학습을 끝까지 마쳤어요' },
      }),
    );
  }, [mode]);

  // ############### route ###############
  const navigate = useNavigate();

  // ############### api ###############
  const todayWorkbookQuery = useTodayClozeWorkbookQuery(
    getClozeTypeBySubject(mode),
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
  );
  const bookmarkWorkbookQuery = useBookmarkClozeWorkbookQuery(
    getClozeTypeBySubject(mode),
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
    isRepeatStudy ? true : undefined,
  );
  const incorrectNoteWorkbookQuery = useIncorrectNoteClozeWorkbookQuery(
    getClozeTypeBySubject(mode),
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
    isRepeatStudy ? true : undefined,
  );

  const noteQuery =
    workbookStudyType === EWorkbookStudyType.INCORRECT_NOTE ? incorrectNoteWorkbookQuery : bookmarkWorkbookQuery;
  const workbookData = useMemo(() => {
    switch (workbookStudyType) {
      case EWorkbookStudyType.BOOKMARK_NOTE:
      case EWorkbookStudyType.INCORRECT_NOTE:
        return noteQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [];
      default:
        return todayWorkbookQuery.data?.data ?? [];
    }
  }, [workbookStudyType, noteQuery.data, todayWorkbookQuery.data]);

  const mutation = useModifyClozeWorkbookMutation(
    getClozeTypeBySubject(mode),
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
  );

  // ############### handler ###############
  const handlePreviousButtonClick = () => {
    if (workbookIdx <= 0) return;
    // playSound('tap');
    setWorkbookIdx((prev) => prev - 1);
  };

  const handleNextButtonClick = async () => {
    // playSound('tap');
    if (isTodayReview && isLastCloze) {
      navigate(IYAGIGO_PATH.CLOZE_HOME_PATH);
      return;
    }
    if (isStudied) {
      // 노트(저장한/틀린 문제) 학습이면 노트 목록으로, 아니면 오늘 홈으로
      navigate(isClozeNote ? createClozePath('note') : IYAGIGO_PATH.CLOZE_HOME_PATH);
      return;
    }

    // 한도 도달 Wall (quota) — 다음 풀이가 한도 초과면 진행 차단
    // 비회원/가입자 한도 동일 → 구독 Wall로 통일 (가입해도 한도 동일하므로)
    const isTodayStudy = workbookStudyType === EWorkbookStudyType.TODAY_STUDY;
    const quota = mode === 'vocabulary' ? GUEST_VOCABULARY_CLOZE_QUOTA : GUEST_GRAMMAR_CLOZE_QUOTA;
    const nextSolvedNumber = workbookIdx + 2;
    if (!isSubscriber && isTodayStudy && nextSolvedNumber > quota) {
      dispatch(
        modalActions.showWallToast({
          type: 'subscription-wall',
          data: { trigger: 'quota', feature: 'cloze', quota, attendanceType: getAttendanceType(studyLanguage, mode) },
        }),
      );
      return;
    }

    setWorkbookIdx((prev) => prev + 1);
    setIsAnswerView(isTodayReview ? true : false);
    resetActiveTime();
  };

  const handleBookmarkClick = async () => {
    if (!isAuthenticated) {
      // playSound('ban');
      dispatch(
        modalActions.showWallToast({
          type: 'signup-wall',
          data: { trigger: 'access' },
        }),
      );
      return;
    }

    // playSound('tap');
    await mutation.mutateAsync({ workbookKey: currentWorkbook.key, isBookmark: !currentWorkbook.isBookmark });
  };

  const handleWordClick = (clickedWordIdx: number) => {
    if (isAnswerView) return;
    setSelectedGrammarWords((prev) => prev.filter((selected) => selected.idx !== clickedWordIdx));
  };

  const handleScoringButtonClick = async () => {
    // mode에 따라 답변 여부 확인
    const studyTime = getActiveTime();
    const now = new Date().toISOString();
    const hasAnswer = mode === 'vocabulary' ? !!selectedChoice : selectedGrammarWords.length > 0;

    if (!hasAnswer) return;

    recordStudy({
      studentKey,
      studyType: mode === 'vocabulary' ? EApiStudyType.VOCABULARY_CLOZE : EApiStudyType.GRAMMAR_CLOZE,
      languageCode: studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
      studyTime,
      studyCount: 1,
    });
    resetActiveTime();

    playSound(isAnswer ? 'correct' : 'incorrect');

    // 다음 복습까지의 지연(밀리초)을 프론트에서 계산 — 백엔드는 받은 값으로 now + reviewAfter 저장
    // isReview(다시보기 누적 학습) 판단은 백엔드와 동일하게 reviewAt 존재 여부로
    const nextReviewAfter = calculateClozeReviewAfter(isAnswer, studyTime, !!currentWorkbook.reviewAt);
    setReviewAfter(nextReviewAfter);

    if (isAnswer) {
      dispatch(
        workbookActions.storeClozeStudyRecord({
          workbookKey: currentWorkbook.key,
          isCorrect: true,
          todayStudiedAt: now,
        }),
      );

      if (!isClozeNote) {
        await mutation.mutateAsync({
          workbookKey: currentWorkbook.key,
          isCorrect: true,
          studyTime: studyTime,
          reviewAfter: nextReviewAfter,
        });
      }
    } else {
      dispatch(
        workbookActions.storeClozeStudyRecord({
          workbookKey: currentWorkbook.key,
          isCorrect: false,
          todayStudiedAt: now,
        }),
      );

      if (!isClozeNote) {
        await mutation.mutateAsync({
          workbookKey: currentWorkbook.key,
          isCorrect: false,
          studyTime: studyTime,
          reviewAfter: nextReviewAfter,
        });
      }
    }

    setIsAnswerView(true);

    if (isTodayStudy && isLastCloze) {
      checkClozeAttendance(getAttendanceType(studyLanguage, mode));
    }

    // 단건(SINGLE) 풀기 종료: 점수 모달 없이 학습 완료 처리
    if (isSingleItem) {
      setIsStudied(true);
      return;
    }

    if (isLastCloze) {
      setIsStudied(true);

      const finalScore = workbookData.reduce((acc, item) => {
        if (item.key === currentWorkbook.key) {
          return acc + (isAnswer ? 1 : 0);
        }
        const record = clozeWorkbookStudyRecord[item.key];
        return acc + (record?.isCorrect ? 1 : 0);
      }, 0);

      dispatch(
        modalActions.showContentModal({
          type: 'study-celebration',
          data: {
            title: '오늘도 해냈어요!',
            description: `<strong>${workbookData.length}문제</strong> 학습을 끝까지 마쳤어요`,
          },
        }),
      );
    }
  };

  const handleConversationStudyClick = () => {
    // playSound('tap');
    dispatch(workbookActions.storeIsClozeConversationStudy(true));
  };

  const handleExplanationButtonClick = () => {
    const getLanguagePrefix = (language: string) => {
      switch (language) {
        case 'english':
          return '[영어]';
        case 'japanese':
          return '[일본어]';
        default:
          return '[한국어]';
      }
    };

    dispatch(
      modalActions.showNotificationToast({
        type: 'cloze-explanation',
        data: {
          clozeType: mode as any,
          answerExplanation: studyLanguageAnswerExplanation,
          addExplanation: studyLanguageAddExplanation,
        },
      }),
    );
  };

  // ############### function ###############
  const convertLevel = (level: string) => {
    switch (level) {
      case 'LEVEL01':
        return 1;
      case 'LEVEL02':
        return 1;
      case 'LEVEL03':
        return 2;
      case 'LEVEL04':
        return 2;
      case 'LEVEL05':
        return 3;
      case 'LEVEL06':
        return 3;
      case 'LEVEL07':
        return 4;
      case 'LEVEL08':
        return 4;
      case 'LEVEL09':
        return 5;
      case 'LEVEL10':
        return 5;
      default:
        return 1;
    }
  };

  const getIsVocabularyAnswer = (userAnswer: string | null, correctAnswer: string[]) => {
    if (!correctAnswer) return false;
    return userAnswer === correctAnswer.join();
  };

  const getIsGrammarAnswer = (
    userSelection: { idx: number; word: string }[],
    correctSentence: string | undefined | null,
  ) => {
    if (!userSelection) return false;
    // selectedGrammarWords는 이제 {word, index}[] 형태이므로 word만 추출
    const wordsOnly = userSelection.map((selected) => selected.word).join('');
    return sanitizeSentence(wordsOnly) === sanitizeSentence(correctSentence ?? '');
  };

  // ############### etc ###############
  const currentWorkbook =
    isSingleItem && currentClozeNoteWorkbookKey
      ? (workbookData.find((item) => item.key === currentClozeNoteWorkbookKey) ?? workbookData[workbookIdx])
      : workbookData[workbookIdx];
  // 진입 시점 학습 간격 라벨 — 문제(key) 단위로만 재계산해 고정.
  // 채점하면 lastStudiedAt이 오늘로 갱신되지만 key는 그대로라, 푸는 동안 라벨이 바뀌지 않음.
  // lastStudiedAt 없음 → 새로운 학습 / 오늘(diff 0) → 오늘 학습 / 그 외 → N일 전 학습
  const lastStudiedLabel = useMemo(() => {
    if (!currentWorkbook?.lastStudiedAt) return '새로운 학습';
    const diffDays = getDiffDays(currentWorkbook.lastStudiedAt);
    return diffDays > 0 ? `${diffDays}일 전 학습` : '오늘 학습';
  }, [currentWorkbook?.key]);
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [selectedGrammarWords, setSelectedGrammarWords] = useState<{ word: string; idx: number }[]>([]);
  const [isAnswerView, setIsAnswerView] = useState(isTodayReview ? true : false);
  const isClozeConversationStudy =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.isClozeConversationStudy) ?? false;
  const [isStudied, setIsStudied] = useState(false);
  // 채점 시 계산한 다음 복습까지 지연(밀리초) — chip 라벨 노출 + mutation 전송에 사용
  const [reviewAfter, setReviewAfter] = useState<number | null>(null);
  const [isBookmarkToolTip, setIsBookmarkToolTip] = useState(false);
  const [nudgeState, setNudgeState] = useState<'open' | 'closing' | 'closed'>('open');
  // 세션 동안 이야기학습을 한 번이라도 시작했으면 nudge 다시 안 보여줌
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  useEffect(() => {
    if (isClozeConversationStudy) setHasStartedConversation(true);
  }, [isClozeConversationStudy]);
  const { isComplete } = useWorkbookSync(workbookData);

  // write mode에서 학습을 다했으면, 초기 서비스 페이지로 이동
  useEffect(() => {
    if (!isClozeNote && !isTodayReview && isComplete) {
      navigate(IYAGIGO_PATH.CLOZE_HOME_PATH);
    }
  }, []);

  useEffect(() => {
    setSelectedChoice(null);
    setSelectedGrammarWords([]);
    setIsAnswerView(isTodayReview ? true : false);
    setIsBookmarkToolTip(false);
    setNudgeState('open');
    setReviewAfter(null);
    // 다른 문제로 넘어갈 때 대화학습 상태도 초기화 (이전 문제의 step 잔재 방지)
    dispatch(workbookActions.resetClozeConversationStudy());
  }, [currentWorkbook?.key]);

  const isLastCloze = workbookIdx === workbookData.length - 1;
  const studyLanguageSentence =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? currentWorkbook?.cloze?.sentenceEn
      : currentWorkbook?.cloze?.sentenceJa;
  const studyLanguageSentencePronunciation =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? currentWorkbook?.cloze?.sentencePronunciationEn
      : currentWorkbook?.cloze?.sentencePronunciationJa;
  const studyLanguageAnswerPronunciation =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? currentWorkbook?.cloze?.answerPronunciationEn
      : currentWorkbook?.cloze?.answerPronunciationJa;
  const studyLanguageSentenceTemplate =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? currentWorkbook?.cloze?.sentenceTemplateEn
      : currentWorkbook?.cloze?.sentenceTemplateJa;
  const studyLanguageAnswer =
    studyLanguage === EApiLanguageCode.ENGLISH ? currentWorkbook?.cloze?.answerEn : currentWorkbook?.cloze?.answerJa;
  const studyLanguageSentenceAudioLink =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? currentWorkbook?.cloze?.sentenceAudioLinkEn
      : currentWorkbook?.cloze?.sentenceAudioLinkJa;
  const studyLanguageChoice =
    studyLanguage === EApiLanguageCode.ENGLISH ? currentWorkbook?.cloze?.choiceEn : currentWorkbook?.cloze?.choiceJa;
  const studyLanguageChoicePronunciation =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? currentWorkbook?.cloze?.choicePronunciationEn
      : currentWorkbook?.cloze?.choicePronunciationJa;

  const studyLanguageAnswerExplanation = currentWorkbook?.cloze?.answerExplanationKo;
  const studyLanguageAddExplanation = currentWorkbook?.cloze?.addExplanationKo;

  // 사용자가 고른 오답에 해당하는 해설 (어휘 모드만 정확한 매칭이 가능)
  const incorrectChoiceExplanation =
    mode === 'vocabulary' && selectedChoice
      ? (studyLanguageAddExplanation?.find((item) => item.keyword === selectedChoice) ?? null)
      : null;

  // ###################################
  // 정답 판단
  // - 빈칸채우기(어휘)면 answer 비교
  // - 빈칸채우기(문법)면 sentence 비교 (특수문자 제거 후 비교)
  // ###################################
  const isAnswer = isTodayReview
    ? true
    : mode === 'vocabulary'
      ? getIsVocabularyAnswer(selectedChoice, studyLanguageAnswer)
      : getIsGrammarAnswer(selectedGrammarWords, studyLanguageSentence);

  // Early return AFTER all hooks to avoid "Rendered more hooks than during the previous render" error
  if (!workbookData.length || !currentWorkbook) {
    return <div></div>;
  }

  const GrammarSentence = selectedGrammarWords.map((selected) => {
    const pronunciation =
      studyLanguage === EApiLanguageCode.JAPANESE
        ? studyLanguageChoicePronunciation?.[studyLanguageChoice?.indexOf(selected.word)]
        : undefined;
    return (
      <TextBlock
        key={uuid4()}
        text={selected.word}
        annotation={pronunciation ? { reading: pronunciation } : undefined}
        reserveAnnotationSpace={studyLanguage === EApiLanguageCode.JAPANESE && !!studyLanguageSentencePronunciation}
        design={createGrammarSentenceWordDesign(!isAnswerView, studyLanguage === EApiLanguageCode.JAPANESE)}
        click={{ handler: () => handleWordClick(selected.idx) }}
      />
    );
  });

  if (isClozeConversationStudy) {
    const isJapanese = studyLanguage === EApiLanguageCode.JAPANESE;
    const sourceConversation = currentWorkbook.cloze.sourceConversation;
    const conversation = isJapanese
      ? (currentWorkbook.cloze.conversationJa ?? currentWorkbook.cloze.conversationKo)
      : (currentWorkbook.cloze.conversationEn ?? currentWorkbook.cloze.conversationKo);

    if (!sourceConversation || !conversation) {
      return null;
    }

    return (
      <ConversationPractice
        sourceKeyword={currentWorkbook.cloze.sourceKeyword ?? ''}
        sourceKeywordPronunciation={currentWorkbook.cloze.sourceKeywordPronunciation ?? ''}
        meaning={currentWorkbook.cloze.keywordMeaningKo ?? ''}
        sourceConversation={sourceConversation}
        conversation={conversation}
        conversationAudioLinks={currentWorkbook.cloze.conversationAudioLink}
        sourceKeywordAudioLink={mode === 'grammar' ? null : currentWorkbook.cloze.sourceKeywordAudioLink}
        answerExplanation={currentWorkbook.cloze.answerExplanationKo}
        conversationVocabularies={conversation.vocabulary}
        conversationGrammars={conversation.grammar}
      />
    );
  }

  // 이야기학습은 sourceConversation 데이터가 있을 때만 노출
  const isConversationStudyAvailable = !!currentWorkbook?.cloze?.sourceConversation;
  const isNudgeShow =
    isAnswerView &&
    nudgeState !== 'closed' &&
    isConversationStudyAvailable &&
    workbookIdx === 0 &&
    !hasStartedConversation;

  return (
    <Wrapper>
      {isNudgeShow && (
        <OtterNudgeContainer $isClosing={nudgeState === 'closing'}>
          <OtterNudge
            copyText={'진짜 대화에선 어떻게 쓰일까요?'}
            goText={'보러가기'}
            handleGoClick={handleConversationStudyClick}
            handleCloseClick={() => {
              setNudgeState('closing');
              setTimeout(() => setNudgeState('closed'), 300);
            }}
          />
        </OtterNudgeContainer>
      )}
      <ClozeContentContainer>
        <MetaContainer>
          <Difficulty currentDifficulty={convertLevel(currentWorkbook.cloze.level)} maxDifficulty={5} />
          <MetaChipContainer>
            {/* 다음 복습 시점 (채점 후, 오늘학습만 — 서버 저장/reviewAt 갱신이 일어나는 모드. 프론트 계산값) */}
            {isAnswerView && isTodayStudy && reviewAfter !== null && (
              <Chip
                text={`${Math.round(reviewAfter / DAY_MS)}일 뒤 다시보기`}
                size='small'
                shape='round'
                width='fit-content'
                usage='static'
                kind='outlined'
                theme={isAnswer ? ETheme.GREEN : ETheme.RED}
              />
            )}
            <Chip
              text={lastStudiedLabel}
              size='small'
              shape='round'
              width='fit-content'
              usage='static'
              theme={ETheme.GRAY}
            />
          </MetaChipContainer>
        </MetaContainer>
        <Gap height={'5px'} />
        <Canvas theme={ETheme.PAPER} shape='round' width='100%' padding='10px' shadow={true}>
          <CardQuestionContainerA>
            <TextBlock
              text={
                mode === 'listening'
                  ? '문장을 듣고 단어를 순서대로 선택해주세요'
                  : mode === 'grammar'
                    ? completeSentence(currentWorkbook.cloze.sentenceTemplateKo, currentWorkbook.cloze.answerKo, false)
                    : normalizeCloze(
                        completeSentence(
                          currentWorkbook.cloze.sentenceTemplateKo,
                          currentWorkbook.cloze.answerKo,
                          true,
                        ),
                      )
              }
              // [임시] GRAMMAR는 3개 언어 정답 일치가 어려워 한국어 정답 하이라이팅 비활성. 복구 시 grammar 분기 제거하고 normalizeCloze만 호출.
              design={QUESTION_TEXT_DESIGN}
            />
          </CardQuestionContainerA>
          <Line color={color.GRAY_2} margin={'5px 0px 10px 0px'} />
          <CardQuestionContainerB>
            {mode === 'vocabulary' && (
              <SentenceContainer>
                <TextBlock
                  text={markBlanks(completeSentence(studyLanguageSentenceTemplate, studyLanguageAnswer, true))}
                  annotation={
                    studyLanguage === EApiLanguageCode.JAPANESE &&
                    studyLanguageSentence &&
                    studyLanguageSentencePronunciation
                      ? { reading: studyLanguageSentencePronunciation }
                      : undefined
                  }
                  blank={isAnswerView ? { textColor: color.GREEN_4 } : undefined}
                  design={QUESTION_STUDY_LANGUAGE_TEXT_DESIGN}
                />
              </SentenceContainer>
            )}
            {mode !== 'vocabulary' && (
              <>
                {mode === 'listening' && (
                  <ImageAudioButton
                    theme={ETheme.BROWN}
                    tone='normal'
                    kind='outlined'
                    size='medium'
                    shape='square'
                    width={CONVERSATION_CONTROL_BUTTON_WIDTH}
                    sources={[studyLanguageSentenceAudioLink]}
                  />
                )}
                {!isAnswerView && selectedGrammarWords.length === 0 && !isTodayReview && (
                  <TextBlock
                    text={'보기에서 단어를 클릭하여 문장을 완성해주세요'}
                    design={{
                      text: { size: 'medium', color: color.GRAY_1, bold: false },
                      // box: { padding: TEXT_PADDING },
                    }}
                  />
                )}
                {!isAnswerView && selectedGrammarWords.length > 0 && (
                  <GrammarSentenceContainer>{GrammarSentence}</GrammarSentenceContainer>
                )}
                {isAnswerView && (
                  <SentenceContainer>
                    <TextBlock
                      text={completeSentence(studyLanguageSentenceTemplate, studyLanguageAnswer, false)}
                      // [임시] GRAMMAR는 빈칸 위치 깨짐이 잦아 학습언어 정답 하이라이팅 비활성. 복구 시 markBlanks(... true) + blank prop 복원.
                      annotation={
                        studyLanguage === EApiLanguageCode.JAPANESE &&
                        studyLanguageSentence &&
                        studyLanguageSentencePronunciation
                          ? { reading: studyLanguageSentencePronunciation }
                          : undefined
                      }
                      design={SENTENCE_TEXT_DESIGN}
                    />
                    {/* 듣기는 문제 화면에 한국어 문장을 보여주지 않으므로(답 노출 방지), 정답 공개 시 번역을 함께 보여준다. */}
                    {mode === 'listening' && currentWorkbook.cloze.sentenceKo && (
                      <>
                        <Gap height={'5px'} />
                        <TextBlock text={currentWorkbook.cloze.sentenceKo} design={TRANSLATION_TEXT_DESIGN} />
                      </>
                    )}
                  </SentenceContainer>
                )}
              </>
            )}
          </CardQuestionContainerB>
        </Canvas>
        <Gap height={size.MARGIN_1} />
        <ChocieScrollContainer>
          <Scroll direction='y' height='100%'>
            {(!isAnswerView || isTodayReview) && (
              <>
                <ChoiceContainer>
                  {mode === 'vocabulary' ? (
                    <VocabularyChoice
                      workbookKey={currentWorkbook.key}
                      choice={studyLanguageChoice}
                      choicePronunciation={
                        studyLanguage === EApiLanguageCode.JAPANESE ? studyLanguageChoicePronunciation : []
                      }
                      answer={studyLanguageAnswer?.join()}
                      isAnswerView={isAnswerView}
                      selectedChoice={selectedChoice}
                      handleChoiceSelect={(choice) => {
                        // playSound('tap');
                        setSelectedChoice(choice);
                      }}
                    />
                  ) : (
                    <GrammarChoice
                      workbookKey={currentWorkbook.key}
                      sentence={studyLanguageSentence}
                      choice={studyLanguageChoice}
                      choicePronunciation={
                        studyLanguage === EApiLanguageCode.JAPANESE ? studyLanguageChoicePronunciation : []
                      }
                      answer={studyLanguageAnswer}
                      isAnswerView={isAnswerView}
                      selectedWords={selectedGrammarWords}
                      handleWordSelect={(word: string, idx: number) => {
                        // playSound('tap');
                        setSelectedGrammarWords((prev) => [...prev, { word, idx }]);
                      }}
                    />
                  )}
                </ChoiceContainer>
                <Gap height={size.MARGIN_1} />
              </>
            )}
            {isAnswerView && (
              <SimpleExplanationContainer
                $backgroundColor={isTodayReview ? color.BLUE_5 : isAnswer ? color.GREEN_5 : color.RED_5}>
                {!isAnswer && mode === 'vocabulary' && incorrectChoiceExplanation && (
                  <>
                    <TextBlock
                      text={'고른 어휘'}
                      design={{
                        text: { size: 'small', color: color.RED_2, bold: true },
                        box: { alignX: 'left' },
                      }}
                    />
                    <Gap height={'5px'} />
                    <Explanation
                      answerExplanation={incorrectChoiceExplanation}
                      clozeType={mode as 'vocabulary' | 'grammar'}
                      labelSize='medium'
                      sections={['answer', 'usage', 'example']}
                    />
                    <Gap height={size.MARGIN_2} />
                  </>
                )}
                {!isAnswer && mode !== 'vocabulary' && (
                  <>
                    <TextBlock
                      text={'만든 문장'}
                      design={{
                        text: { size: 'small', color: color.RED_2, bold: true },
                        box: { alignX: 'left' },
                      }}
                    />
                    <Gap height={'5px'} />
                    <GrammarSentenceContainer>{GrammarSentence}</GrammarSentenceContainer>
                    <Gap height={'5px'} />
                  </>
                )}
                {!isAnswer && mode === 'vocabulary' && (
                  <>
                    <TextBlock
                      text={'정답 어휘'}
                      design={{
                        text: { size: 'small', color: color.GREEN_3, bold: true },
                        box: { alignX: 'left' },
                      }}
                    />
                    <Gap height={'5px'} />
                  </>
                )}
                <Explanation
                  answerExplanation={studyLanguageAnswerExplanation}
                  clozeType={mode as 'vocabulary' | 'grammar'}
                  labelSize='medium'
                  sections={['answer', 'usage', 'example']}
                />
              </SimpleExplanationContainer>
            )}
          </Scroll>
        </ChocieScrollContainer>
      </ClozeContentContainer>
      <Gap height={size.MARGIN_1} />
      <BottomContainer
        position='relative'
        // height={BOTTOM_CONTAINER_HEIGHT}
        height='auto'
        backgroundColor={color.WHITE_1}
        margin={`0 -${size.PAGE_PADDING}`}
        animationKey={isAnswerView ? 'answer' : 'question'}>
        <BottomContentContainer>
          {isAnswerView && (
            <ControlContainer>
              <FirstRowContainer>
                {isConversationStudyAvailable && (
                  <ImageTextBlock
                    src={otterReadImage}
                    alt='이야기 학습'
                    text='이야기 학습'
                    design={{
                      image: { width: '24px', height: '24px' },
                      text: { size: 'medium', color: color.BROWN_1, bold: true },
                      box: {
                        gap: '4px',
                        padding: '4px 8px 4px 4px',
                        borderColor: color.BROWN_2,
                        borderWidth: size.BORDER_WIDTH_1,
                        borderRadius: size.BORDER_RADIUS_2,
                        backgroundColor: color.WHITE_1,
                        event: { cursor: 'pointer' },
                      },
                    }}
                    click={{ handler: handleConversationStudyClick }}
                  />
                )}
                <BookmarkContainer>
                  <IconTextBlock
                    icon='bookmark'
                    text={currentWorkbook.isBookmark ? '저장완료' : '저장하기'}
                    arrangement='icon-text'
                    design={{
                      box: {
                        event: { cursor: 'pointer' },
                        width: '80px',
                        alignX: 'left',
                        alignY: 'bottom',
                      },
                      text: {
                        size: 'medium',
                        color: currentWorkbook.isBookmark ? color.YELLOW_1 : color.BROWN_2,
                      },
                      icon: {
                        size: 'medium',
                        color: currentWorkbook.isBookmark ? color.YELLOW_1 : color.BROWN_2,
                        kind: currentWorkbook.isBookmark ? 'filled' : 'outlined',
                      },
                    }}
                    click={{ handler: handleBookmarkClick }}
                  />
                  <IconBlock
                    icon='info'
                    design={{
                      icon: { size: 'small', color: color.GRAY_2 },
                      box: { event: { cursor: 'pointer' } },
                    }}
                    click={{ handler: () => setIsBookmarkToolTip((prev) => !prev) }}
                  />
                  {isBookmarkToolTip && (
                    <BookmarkCalloutContainer>
                      <Callout
                        text='반복학습에서 다시 풀 수 있어요'
                        direction='bottom'
                        size='tiny'
                        theme={ETheme.GRAY}
                      />
                    </BookmarkCalloutContainer>
                  )}
                </BookmarkContainer>
              </FirstRowContainer>
              <Gap height={size.MARGIN_1} />
              <ControlRowContainer>
                <StudyContainer>
                  <ImageAudioButton
                    theme={ETheme.BROWN}
                    tone='normal'
                    kind='outlined'
                    size='medium'
                    shape='square'
                    width={CONVERSATION_CONTROL_BUTTON_WIDTH}
                    sources={[studyLanguageSentenceAudioLink]}
                  />
                  <IconButton
                    icon='text-snippet'
                    theme={ETheme.BROWN}
                    tone='normal'
                    kind='outlined'
                    size='medium'
                    shape='square'
                    width={CONVERSATION_CONTROL_BUTTON_WIDTH}
                    handleClick={handleExplanationButtonClick}
                  />
                </StudyContainer>
                {isAnswerView && (
                  <NavigationButtonContainer>
                    {isTodayReview && workbookIdx > 0 && (
                      <TextButton
                        theme={ETheme.BROWN}
                        size='medium'
                        shape='square'
                        width={NAVIGATION_BUTTON_WIDTH}
                        text={'이전문제'}
                        handleClick={handlePreviousButtonClick}
                      />
                    )}
                    <TextButton
                      theme={ETheme.BROWN}
                      size='medium'
                      shape='square'
                      width={NAVIGATION_BUTTON_WIDTH}
                      text={isStudied || (isTodayReview && isLastCloze) ? '끝내기' : '다음문제'}
                      handleClick={handleNextButtonClick}
                    />
                  </NavigationButtonContainer>
                )}
              </ControlRowContainer>
            </ControlContainer>
          )}
          {isAnswerView ? (
            <TextButton
              theme={isTodayReview ? ETheme.BLUE : isAnswer ? ETheme.GREEN : ETheme.RED}
              size='medium'
              shape='pill'
              width={'100%'}
              text={isTodayReview ? '다시 볼수록 내 것이 돼요!' : isAnswer ? '정답이에요' : '오답이에요'}
              icon={isTodayReview ? undefined : isAnswer ? 'circle' : 'close'}
            />
          ) : (
            <TextButton
              theme={ETheme.BROWN}
              size='medium'
              shape='pill'
              width={'100%'}
              text={'채점하기'}
              disabled={mode === 'vocabulary' ? !selectedChoice : selectedGrammarWords.length === 0}
              handleClick={handleScoringButtonClick}
            />
          )}
        </BottomContentContainer>
      </BottomContainer>
    </Wrapper>
  );
};

export default Cloze;
